<template>
  <div>
    <div v-if="!loading" id="checkout-summary">
      <div class="info-box">
        <div class="info-inner-box" v-if="!isMobile()">
          <UserInfoDisplay
            v-if="userInfo"
            class="user-info"
            :userInfo="userInfo"
            @updateData="showUpdateData"
          />
          <PaymentSummaryList
            :loading="summarySkeleton"
            class="payment-info-list"
            :installmentSelected="installmentSelected"
            v-if="installmentSelected"/>
          <Button v-if="!isMobile()" class="link" @click="showUpdateData">
            Alterar dados
          </Button>
          <div class="info-inner-footer">
            <Button
              class="info-inner-footer-button primary"
              @click="handleFinishTransaction"
              :disabled="summarySkeleton">
              Finalizar compra
            </Button>
          </div>
        </div>

        <div v-if="isMobile()">
          <div class="info-inner-box no-margin-top">
            <UserInfoDisplay
              v-if="userInfo"
              class="user-info"
              :userInfo="userInfo"
            />
          </div>

          <div class="info-inner-box" v-if="showProductListMobile">
            <div class="product-box">
              <div class="product-box-title">Produtos</div>
              <ProductList
                v-if="order && order.products"
                :products="order.products"
                />
            </div>
          </div>

          <div class="info-inner-box">
            <div class="payment-info-header">
              <h3 class="gray-600">Parcelamento</h3>
              <Button class="link" @click="showUpdateData">
                Editar
              </Button>
            </div>
            <PaymentSummaryList
              :loading="summarySkeleton"
              class="payment-info-list"
              :installmentSelected="installmentSelected"
              v-if="installmentSelected"/>
            <div class="info-inner-footer">

              <Button
                class="info-inner-footer-button primary"
                @click="handleFinishTransaction"
                :disabled="summarySkeleton">
                Finalizar compra
              </Button>
            </div>

            <div class="info-footer">
              <Button class="link" :href="backUrl" @click="handleReturn">
                Cancelar e voltar para loja
              </Button>
            </div>
          </div>
        </div>

        <div class="info-footer" v-if="!isMobile()">
          <Button class="link" :href="backUrl" @click="handleReturn">
            Cancelar e voltar para loja
          </Button>
        </div>
      </div>
      <div class="product-box" v-if="!showProductListMobile">
        <div  v-if="order && order.products && order.products.length < 2"
          class="product-box-title">Produto comprado</div>
        <div  v-else class="product-box-title">Produtos comprados</div>
        <ProductList
          v-if="order && order.products"
          :products="order.products"
          />
      </div>
      <modal
        :styles="isMobile() ? 'overflow-y: scroll;' : ''"
        name="update-data"
        :width="isMobile() ? '100%' : '640px'"
        :height="isMobile() ? '100%' : 'auto'"
      >
        <Icon
          id="close-button-update-modal"
          name="times"
          @click.native="hideUpdateData"
          class="cursor-pointer"/>
        <UpdateDataSummary
          @cancel="hideUpdateData"
          @update="handleUpdateData"
          :userInfo="userInfo"/>
      </modal>
    </div>
    <LockLoader :text="loaderText" v-else/>
  </div>
</template>

<script>
import querystring from 'querystring';
import axios from 'axios';
import { mapState, mapGetters, mapActions } from 'vuex';
import debug from 'debug';
import StringMixin from '@/mixins/string';
import UtilMixin from '@/mixins/util';
import Button from '@/components/ui/AppButton.vue';
import Icon from '@/components/ui/AppIcon.vue';
import LockLoader from '@/components/common/LockLoader.vue';
import ProductList from '@/components/common/ProductList.vue';
import Logger from '@/lib/logger';
import UserInfoDisplay from '../components/UserInfoDisplay.vue';
import PaymentSummaryList from '../components/PaymentSummaryList.vue';
import UpdateDataSummary from '../components/UpdateDataSummary.vue';
import StoreApi from '../../../services/store';

const log = debug('vue:checkout:summary');

export default {
  name: 'checkout-summary',
  metaInfo: {
    title: 'Sumário',
  },
  data: () => ({
    loaderText: 'Carregando seu pedido no Parcelex',
    loading: true,
    backUrl: null,
    confirmUrl: null,
    name: '',
  }),
  mixins: [StringMixin, UtilMixin],
  components: { UserInfoDisplay,
    PaymentSummaryList,
    UpdateDataSummary,
    ProductList,
    Button,
    Icon,
    LockLoader },
  methods: {
    ...mapActions('checkout', [
      'resetLoading',
      'setInstallmentIndex',
      'setDownPaymentIndex',
      'checkoutValidate',
      'reload',
      'emitEvent',
    ]),
    showUpdateData() {
      this.$modal.show('update-data');
    },
    hideUpdateData() {
      this.$modal.hide('update-data');
    },
    handleUpdateData({ downPaymentIndex, installmentIndex }) {
      this.hideUpdateData();
      this.setDownPaymentIndex(downPaymentIndex);
      this.setInstallmentIndex(installmentIndex);
    },
    handleReturn() {
      this.loaderText = '';
      this.loading = true;
    },
    async handleFinishTransaction() {
      this.loading = true;
      const optionId = this.installmentSelected._id;
      try {
        const query = querystring.stringify({ optionId });
        const { data } = await axios.get(`${this.confirmUrl}?${query}`);
        this.$router.replace({
          name: 'onboarding-main',
          query: {
            transactionId: data.transactionId,
            onboardingToken: data.onboardingToken,
          },
        });
      } catch (err) {
        Logger.error(err);
        this.$router.replace({ name: 'checkout-error' });
      }
    },
  },
  computed: {
    ...mapState('checkout', ['user', 'order', 'summarySkeleton', 'checkoutId']),
    ...mapGetters('checkout', ['installmentSelected', 'ccbUrl']),
    showProductListMobile() {
      return this.isMobile() && this.order && this.order.products;
    },
    userInfo() {
      if (!this.user) return null;
      return {
        Nome: this.name ? this.capitalize((this.name.split(' ')[0])) : '',
        CPF: this.cpfFilter(this.user.cpf),
        Telefone: this.phoneFilter(this.user.phone),
        Email: this.user.email,
      };
    },
  },
  mounted() {
    const { checkoutId, name } = this.$route.query;
    this.name = name || '';
    this.backUrl = this.$route.query.backUrl;
    this.confirmUrl = this.$route.query.confirmUrl;

    this.emitEvent({
      type: 'show',
      page: 'overview',
    });

    this.checkoutValidate(checkoutId)
      .then(() => {
        this.loading = false;
      })
      .catch(async () => {
        if (checkoutId) {
          const { data } = await StoreApi.get(`/store/${checkoutId}`);
          this.reload(data);
          this.loading = false;
        } else {
          throw new Error('checkoutId not defined');
        }
      })
      .catch((err) => {
        const info = `checkoutId: ${checkoutId}, this.checkoutId ${this.checkoutId}`;
        log(info);
        Logger.error(err, info);
        this.$router.replace({ name: 'checkout-error' });
      })
      .finally(() => {
        this.resetLoading();
      });
  },
};
</script>

<style lang="scss" scoped>
// some mobiles 400px+ has 2px left auto set
::v-deep .vm--modal {
  @media screen and (max-width: $md) {
    left: 0px !important;
  }
}

#close-button-update-modal {
  float: right;
  padding: 12px;
  width: 20px;
  height: 20px;
  @media (max-width: $md) {
    position: fixed;
    right: 0;
    z-index: 5;
  }
}

#checkout-summary {
  background-color: var(--gray-100);
  width: 100%;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  box-sizing: border-box;
  padding-top: 31px;
  @media (max-width: $md) {
    flex-direction: column;
    padding-top: 0px;
  }
}

.payment-info-list {
  @media (max-width: $md) {
    background-color: white;
    padding: 0;
  }
}

.info-box {
  @media (min-width: $md) {
    padding-right: 20px;
    width: 676px;
    height: fit-content;
  }
}

.payment-info-header {
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  @media (max-width: $md) {
    padding-bottom: 12px;
  }
}

.info-inner-box {
  border: 1px solid var(--gray-300);
  padding: 20px 24px;
  background: var(--white);
  border-radius: 4px;
  .link {
    padding: 0;
  }
  @media (max-width: $md) {
    margin-top: 8px;
    border-radius: 0px;
    border: 0px;
    padding-bottom: 0;
  }
}

.info-inner-footer {
  width: 100%;
  padding-top: 24px;
  @media (min-width: $lg) {
    padding-top: 0;
    display: flex;
    /* justify-content: flex-end; */
    justify-content: space-between;
  }
}

.user-info-footer-disclaimer {
  @extend .small-regular;
  @extend .gray-600;
  align-self: center;
  @media (max-width: $md) {
    padding-bottom: 24px;
    padding-top: 18px;
  }
}

.info-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 14px;
}
.info-inner-footer-button {
  @media (max-width: $md) {
    width: 100%;
    height: 48px;
  }
}

.link {
  @extend .small-regular;
  @media (max-width: $md) {
    display: flex;
  }
}

.product-box {
  width: 328px;
  @media (max-width: $md) {
    position: relative;
    width: 100%;
  }
}

.product-box-title {
  @extend .h3;
  @extend .gray-600;
  padding-bottom: 16px;
  @media (max-width: $md) {
    padding: 0;
    position: relative;
    background: var(--white);
  }
}

.ccb-text {
  @extend .small-regular;
  margin: 8px 0;
  text-align: center;

  a {
    @extend .primary;
    text-decoration: none;
  }
}
</style>
